import type { CommonData } from '@/components/blockEditor/blockEditor.types';
import { CURRENT_SCHEMA_VERSION } from '@/components/blockEditor/blockEditor.types';

export function getDefaultIdgtOverviewPageOneData(): CommonData {
  return {
    version: CURRENT_SCHEMA_VERSION,
    blocks: [
      {
        id: 'description',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'idgt-overview-heading',
              type: 'EducationalSlideHeading',
              props: {
                title: 'What is an IDGT?',
                variant: 'h3',
              },
            },
            {
              id: 'idgt-overview-body',
              type: 'EditableEducationalSlideBody',
              props: {
                text: 'An IDGT is an irrevocable trust intentionally drafted with a tax "defect" \u2014 assets are transferred out of the grantor\'s estate for estate and gift tax purposes, while the grantor remains responsible for paying the trust\'s income taxes. By shifting the income tax burden to the grantor personally, trust assets grow without tax drag, and each dollar the grantor pays in taxes further reduces their taxable estate, all without gift tax consequence.',
              },
            },
          ],
        },
      },
      {
        id: 'benefits',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'idgt-overview-page-one-columns',
              type: 'TwoColumnLayout',
              props: {
                leftWidth: '50%',
                rightWidth: '50%',
                columnGap: 4,
                showDivider: true,
              },
              children: {
                leftColumn: [
                  {
                    id: 'idgt-overview-benefits-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Potential benefits',
                      variant: 'h2',
                    },
                  },
                  {
                    id: 'idgt-benefit-tax-efficient-growth',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Tax-efficient growth',
                      description:
                        'Trust assets grow without being reduced by income taxes, which are paid personally by the grantor rather than from trust assets \u2014 and those payments are not treated as additional taxable gifts.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'idgt-benefit-estate-reduction',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Estate reduction',
                      description:
                        "The grantor's payment of trust income taxes further reduces their taxable estate with no gift tax consequence.",
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'idgt-benefit-wealth-transfer',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Wealth transfer efficiency',
                      description:
                        'An installment sale to an IDGT \u2014 where the grantor sells assets in exchange for a promissory note at the AFR \u2014 removes future appreciation from the estate immediately, with no capital gain recognized on the transfer and minimal use of lifetime exemption.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'idgt-benefit-asset-substitution',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Asset substitution',
                      description:
                        "The grantor can swap assets of equivalent value with the trust without triggering capital gains, providing ongoing flexibility to actively manage the trust's asset composition.",
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                ],
                rightColumn: [
                  {
                    id: 'idgt-overview-drawbacks-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Potential drawbacks',
                      variant: 'h2',
                    },
                  },
                  {
                    id: 'idgt-drawback-irrevocability',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Irrevocability',
                      description:
                        'The transfer of assets into an IDGT is permanent \u2014 the grantor relinquishes control and cannot unwind the transfer if circumstances change.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'idgt-drawback-no-step-up',
                    type: 'PresentationListItem',
                    props: {
                      title: 'No step-up in basis',
                      description:
                        "Assets held in an IDGT do not receive a stepped-up basis at the grantor's death, leaving beneficiaries with potential capital gains exposure on future sales. This risk can be actively managed \u2014 the grantor's asset substitution power allows low-basis trust assets to be swapped back into the grantor's estate prior to death, capturing a step-up in basis at that time.",
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'idgt-drawback-grantor-liquidity',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Grantor liquidity',
                      description:
                        "The grantor must have sufficient resources outside the trust to cover the trust's income tax liability on an ongoing basis \u2014 a thorough cash flow analysis is essential before funding.",
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'idgt-drawback-afr-breakeven',
                    type: 'PresentationListItem',
                    props: {
                      title: 'AFR breakeven',
                      description:
                        'For installment sales, trust assets must appreciate faster than the AFR or the strategy loses its advantage.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                ],
              },
            },
          ],
        },
      },
    ],
  };
}
